import type { LucideIcon } from "lucide-react";

export interface SegmentOption<T extends string> {
  key: T;
  label: string;
  icon: LucideIcon;
  count?: number;
}

interface SegmentStripProps<T extends string> {
  value: T;
  onChange: (next: T) => void;
  options: SegmentOption<T>[];
  ariaLabel: string;
}

/** Shared segment / tab chip strip (Client Dashboard Settings pattern). */
export function SegmentStrip<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
}: SegmentStripProps<T>) {
  return (
    <div className="menu-segment-strip" role="tablist" aria-label={ariaLabel}>
      {options.map(({ key, icon: Icon, label, count }) => {
        const active = value === key;
        const muted = typeof count === "number" && count === 0;
        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={active}
            className={`menu-segment-chip ${active ? "menu-segment-chip--active" : ""}`}
            onClick={() => onChange(key)}
          >
            <span className="menu-segment-chip__icon" aria-hidden>
              <Icon className="h-4 w-4" strokeWidth={2.25} />
            </span>
            <span className="menu-segment-chip__label">{label}</span>
            {typeof count === "number" ? (
              <span
                className={`menu-segment-chip__count ${
                  muted ? "menu-segment-chip__count--muted" : ""
                }`}
              >
                {count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
